// Estado de falha da lista e do detalhe — um só markup para não divergir.
// Só o erro transitório oferece retry; acesso negado e "não encontrada" não se
// resolvem tentando de novo.
import type { LucideIcon } from "lucide-react"
import {
  FileQuestionIcon,
  ServerOffIcon,
  ShieldOffIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/commons/empty-state"
import { Button } from "@/components/ui/button"

import type { FeedbackVariant } from "../feedback"

const prefix = "pages.paymentConfigurations"

interface FeedbackConfig {
  icon: LucideIcon
  /** Anunciado por leitor de tela ao aparecer. */
  isAlert: boolean
}

const FEEDBACK_CONFIG: Record<
  FeedbackVariant,
  FeedbackConfig
> = {
  forbiddenScope: { icon: ShieldOffIcon, isAlert: true },
  forbidden: { icon: ShieldOffIcon, isAlert: true },
  notFound: { icon: FileQuestionIcon, isAlert: false },
  error: { icon: ServerOffIcon, isAlert: true },
}

interface PaymentConfigurationFeedbackProps {
  variant: FeedbackVariant
  /** Só usado no `error`. */
  onRetry?: () => void
  className?: string
}

export const PaymentConfigurationFeedback = ({
  variant,
  onRetry,
  className,
}: PaymentConfigurationFeedbackProps) => {
  const { t } = useTranslation()
  const { icon: Icon, isAlert } = FEEDBACK_CONFIG[variant]

  // Chaves literais por variante: o guard de i18n só enxerga `t("...")` estático.
  const texts: Record<
    FeedbackVariant,
    { title: string; description: string }
  > = {
    forbiddenScope: {
      title: t(`${prefix}.forbiddenScope.title`),
      description: t(
        `${prefix}.forbiddenScope.description`
      ),
    },
    forbidden: {
      title: t(`${prefix}.forbidden.title`),
      description: t(`${prefix}.forbidden.description`),
    },
    notFound: {
      title: t(`${prefix}.detail.notFoundTitle`),
      description: t(
        `${prefix}.detail.notFoundDescription`
      ),
    },
    error: {
      title: t(`${prefix}.error.title`),
      description: t(`${prefix}.error.description`),
    },
  }
  const { title, description } = texts[variant]

  return (
    <EmptyState
      icon={Icon}
      title={title}
      description={description}
      role={isAlert ? "alert" : undefined}
      data-testid={`payment-configuration-feedback-${variant}`}
      className={className}
      action={
        variant === "error" &&
        onRetry && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRetry}
          >
            {t(`${prefix}.error.retry`)}
          </Button>
        )
      }
    />
  )
}
